import { type Dispatch } from "redux";
import { type FiltersMap } from "utils/filterUtils";
import accountsDataService from "../../../../Accounts/services/accountsDataService";
import { makeActionCreator } from "features/Application/actions/actionsBuilder";
import rolesDataService from "../../../services/rolesDataService";
import * as filterActionTypes from "./filterActionTypes";
import DataService from "features/Application/services/dataServices/typedDataService";
import { type AxiosResponse } from "axios";
import { type UsersFilterOptions } from "features/People/types";

export interface FilterOptionParams {
  shouldGetRoles: boolean;
  roleSearchQuery: string;
  shouldGetOfficeLocations: boolean;
  shouldGetManagers: boolean;
  shouldGetLicenses: boolean;
  shouldGetDefaultRoles: boolean;
  accountTypeId: number;
  shouldGetGroups: boolean;
}

export const setUserFilter = makeActionCreator(filterActionTypes.PEOPLE_USERS_OVERVIEW_SET_ITEMS_FILTER, "filter");

export const resetUserFilter = makeActionCreator(filterActionTypes.PEOPLE_USERS_OVERVIEW_RESET_ITEMS_FILTER);

export const getFilterOptions = (filterOptionParams?: FilterOptionParams) => {
  filterOptionParams = filterOptionParams || {
    shouldGetRoles: false,
    roleSearchQuery: "",
    shouldGetOfficeLocations: false,
    shouldGetManagers: false,
    shouldGetLicenses: false,
    shouldGetDefaultRoles: false,
    accountTypeId: 1,
    shouldGetGroups: false,
  };
  const getFilterOptionsBegin = () => ({
    type: filterActionTypes.PEOPLE_USERS_OVERVIEW_FILTER_OPTIONS.FETCH_OPTIONS_BEGIN,
  });

  const getFilterOptionsSuccess = (filterOptions: FiltersMap) => ({
    type: filterActionTypes.PEOPLE_USERS_OVERVIEW_FILTER_OPTIONS.FETCH_OPTIONS_SUCCESS,
    payload: { ...filterOptions },
  });

  const getFilterOptionsFailed = (error: Error) => ({
    type: filterActionTypes.PEOPLE_USERS_OVERVIEW_FILTER_OPTIONS.FETCH_OPTIONS_FAILURE,
    payload: { error },
  });

  const mapRoles = (data: { items: { name: string; id: number }[] }) =>
    data.items.map((x) => ({ text: x.name, value: x.id }));

  const mapData = (data: AxiosResponse<string[]>) =>
    data.data.filter((x: string) => x !== "" && x !== null).map((x: string) => ({ text: x, value: x }));

  const mapLicenses = (data: { title: string; skuId: string }[]) =>
    data.map((x) => ({ text: x.title, value: x.skuId }));

  const mapDefaultRoles = (data: { name: string }[]) => data.map((x) => ({ text: x.name, value: x.name }));

  const mapGroups = (data: { name: string; id: string }[]) => data.map((x) => ({ text: x.name, value: x.id }));

  return async (dispatch: Dispatch) => {
    dispatch(getFilterOptionsBegin());

    try {
      let roles = null;
      let officeLocations = null;
      let managers = null;
      let licenses = null;
      let defaultRoles = null;
      let groups = null;

      if (filterOptionParams.shouldGetRoles) {
        const result = await rolesDataService.getRoles(filterOptionParams.roleSearchQuery);
        roles = mapRoles(result);
      }
      if (filterOptionParams.shouldGetOfficeLocations) {
        const result = await DataService.users.getOfficeLocations();
        officeLocations = mapData(result);
      }
      if (filterOptionParams.shouldGetManagers) {
        const result = await DataService.users.getManagers();
        managers = mapData(result);
      }
      if (filterOptionParams.shouldGetLicenses) {
        const result = await DataService.users.getLicenses();
        licenses = mapLicenses(result);
      }
      if (filterOptionParams.shouldGetDefaultRoles) {
        const result = await accountsDataService.getAccountTypeRoles(filterOptionParams.accountTypeId);
        defaultRoles = mapDefaultRoles(result);
      }
      if (filterOptionParams.shouldGetGroups) {
        const result = await DataService.users.getAccountGroups();
        groups = mapGroups(result);
      }

      const departments = await DataService.users.getDepartments();
      const jobTitles = await DataService.users.getJobTitles();

      const filterOptions: UsersFilterOptions = {
        departments: mapData(departments),
        jobTitles: mapData(jobTitles),
        officeLocations,
        managers,
        roles,
        licenses,
        defaultRoles,
        groups,
      };

      dispatch(getFilterOptionsSuccess(filterOptions));
    } catch (error) {
      dispatch(getFilterOptionsFailed(error));
    }
  };
};
